// src/features/dashboard/components/ManagerDashboard/IdleTimeReport/DailyIdleTrendChart.tsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';

import type {
  IdleTrendResponse,
} from '../../../types/Manager/idleTime.types';

/* ============================================================
 * TYPES
 * ============================================================ */

interface Props {
  data: IdleTrendResponse[];
}

type TrendRow = IdleTrendResponse & {
  teamLeadName?: string;
};

interface LegendItem {
  value: string;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: readonly any[];
  label?: string;
}

/* ============================================================
 * CONSTANTS
 * ============================================================ */

const TEAM_COLORS = [
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#22C55E',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#6366F1',
  '#84CC16',
];

/*
 * Target percentage.
 */
const TARGET_PERCENTAGE = 14;

/*
 * Maximum Y-axis value.
 */
const MAX_Y_VALUE = 30;

/*
 * Chart height.
 */
const CHART_HEIGHT = 280;

/*
 * Y-axis width.
 */
const Y_AXIS_WIDTH = 52;

/*
 * Width allocated for every date.
 */
const DATE_COLUMN_WIDTH = 90;

/*
 * IMPORTANT:
 *
 * Recharts reserves height for the XAxis.
 *
 * Chart height = 280
 * Top margin   = 10
 * Bottom margin = 20
 * XAxis height = 30
 *
 * Actual plotting area:
 *
 * 280 - 10 - 20 - 30 = 220
 *
 * The ReferenceLine uses this plotting area.
 */
const X_AXIS_HEIGHT = 30;

const CHART_MARGIN = {
  top: 10,
  right: 65,
  bottom: 20,
  left: 0,
};

const PLOT_HEIGHT =
  CHART_HEIGHT -
  CHART_MARGIN.top -
  CHART_MARGIN.bottom -
  X_AXIS_HEIGHT;

/* ============================================================
 * TOOLTIP
 * ============================================================ */

const CustomTooltip = ({
  active,
  payload,
  label,
}: CustomTooltipProps) => {
  if (
    !active ||
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  return (
    <div
      className="
        min-w-[210px]
        max-w-[260px]
        overflow-hidden
        rounded-xl
        border
        border-slate-200
        bg-white
        shadow-xl
      "
    >
      <div
        className="
          border-b
          border-slate-200
          bg-slate-50
          px-3
          py-2
        "
      >
        <p
          className="
            text-xs
            font-semibold
            text-slate-800
          "
        >
          {label}
        </p>
      </div>

      <div className="space-y-2 p-3">
        {payload.map(
          (item, index) => {
            const value = Number(
              item?.value ?? 0,
            );

            const color =
              item?.color ??
              '#94A3B8';

            return (
              <div
                key={`${item?.dataKey}-${index}`}
                className="
                  flex
                  items-center
                  justify-between
                  gap-5
                "
              >
                <div
                  className="
                    flex
                    min-w-0
                    items-center
                    gap-2
                  "
                >
                  <span
                    className="
                      h-2.5
                      w-2.5
                      shrink-0
                      rounded-full
                    "
                    style={{
                      backgroundColor:
                        color,
                    }}
                  />

                  <span
                    className="
                      min-w-0
                      truncate
                      text-[11px]
                      font-medium
                      text-slate-600
                    "
                  >
                    {item?.name ??
                      item?.dataKey}
                  </span>
                </div>

                <span
                  className="
                    shrink-0
                    text-[11px]
                    font-semibold
                    text-slate-800
                  "
                >
                  {value.toFixed(1)}%
                </span>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

/* ============================================================
 * LEGEND
 * ============================================================ */

interface CustomLegendProps {
  payload?: readonly LegendItem[];

  hoveredTeam: string | null;

  onHoverTeam: (
    team: string | null,
  ) => void;
}

const CustomLegend = ({
  payload,
  hoveredTeam,
  onHoverTeam,
}: CustomLegendProps) => {
  if (
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  return (
    <div
      className="
        flex
        w-full
        flex-wrap
        items-center
        justify-center
        gap-x-5
        gap-y-1
        px-2
        pb-2
      "
    >
      {payload.map(
        (item, index) => {
          const teamName =
            item.value;

          const isHovered =
            hoveredTeam ===
            teamName;

          const hasHover =
            hoveredTeam !== null;

          const isFaded =
            hasHover &&
            !isHovered;

          return (
            <button
              key={`${teamName}-${index}`}
              type="button"
              className="
                group
                flex
                cursor-pointer
                items-center
                gap-1.5
                rounded-md
                px-1.5
                py-1
                outline-none
                transition-all
                duration-150
              "
              style={{
                opacity: isFaded
                  ? 0.35
                  : 1,
              }}
              onMouseEnter={() =>
                onHoverTeam(
                  teamName,
                )
              }
              onMouseLeave={() =>
                onHoverTeam(null)
              }
            >
              <span
                className="
                  h-3
                  w-3
                  shrink-0
                  rounded-full
                  transition-transform
                  duration-150
                "
                style={{
                  backgroundColor:
                    item.color,

                  transform:
                    isHovered
                      ? 'scale(1.25)'
                      : 'scale(1)',
                }}
              />

              <span
                className="
                  max-w-[180px]
                  truncate
                  text-[11px]
                  font-medium
                  transition-all
                  duration-150
                "
                style={{
                  color: isHovered
                    ? item.color
                    : isFaded
                      ? '#94A3B8'
                      : '#475569',

                  fontWeight:
                    isHovered
                      ? 700
                      : 500,
                }}
              >
                {teamName}
              </span>
            </button>
          );
        },
      )}
    </div>
  );
};

/* ============================================================
 * MAIN COMPONENT
 * ============================================================ */

const DailyIdleTrendChart = ({
  data,
}: Props) => {
  /* ==========================================================
   * REFS
   * ========================================================== */

  const scrollRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  const plotWrapperRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  /* ==========================================================
   * STATE
   * ========================================================== */

  const [
    availableWidth,
    setAvailableWidth,
  ] = useState(0);

  const [
    hoveredTeam,
    setHoveredTeam,
  ] = useState<string | null>(
    null,
  );

  const [
    isDragging,
    setIsDragging,
  ] = useState(false);

  /* ==========================================================
   * DRAG REFS
   * ========================================================== */

  const dragStartX =
    useRef(0);

  const dragScrollLeft =
    useRef(0);

  /* ==========================================================
   * RESPONSIVE WIDTH
   *
   * Works with:
   *
   * 50%
   * 67%
   * 80%
   * 90%
   * 100%
   *
   * and browser resizing.
   * ========================================================== */

  useEffect(() => {
    const element =
      plotWrapperRef.current;

    if (!element) {
      return;
    }

    const updateWidth = () => {
      const width =
        element.getBoundingClientRect()
          .width;

      if (width > 0) {
        setAvailableWidth(width);
      }
    };

    updateWidth();

    const observer =
      new ResizeObserver(() => {
        updateWidth();
      });

    observer.observe(element);

    window.addEventListener(
      'resize',
      updateWidth,
    );

    return () => {
      observer.disconnect();

      window.removeEventListener(
        'resize',
        updateWidth,
      );
    };
  }, []);

  /* ==========================================================
   * NORMALIZE DATA
   * ========================================================== */

  const normalizedData =
    useMemo<TrendRow[]>(() => {
      if (!Array.isArray(data)) {
        return [];
      }

      return data
        .filter(Boolean)
        .map((item) => {
          const row =
            item as TrendRow;

          return {
            ...row,

            teamLeadName:
              row.teamLeadName ??
              'Team',
          };
        });
    }, [data]);

  /* ==========================================================
   * TEAMS
   * ========================================================== */

  const teams = useMemo(
    () => {
      const names =
        new Set<string>();

      normalizedData.forEach(
        (item) => {
          names.add(
            item.teamLeadName ||
              'Team',
          );
        },
      );

      return Array.from(names);
    },
    [normalizedData],
  );

  /* ==========================================================
   * DATES
   * ========================================================== */

  const dates = useMemo(
    () => {
      const dateSet =
        new Set<string>();

      normalizedData.forEach(
        (item) => {
          if (!item.loggedDate) {
            return;
          }

          dateSet.add(
            String(
              item.loggedDate,
            ).slice(0, 10),
          );
        },
      );

      return Array.from(
        dateSet,
      ).sort();
    },
    [normalizedData],
  );

  /* ==========================================================
   * CHART DATA
   * ========================================================== */

  const chartData = useMemo(
    () => {
      return dates.map(
        (date) => {
          const row: Record<
            string,
            string | number
          > = {
            date,
          };

          teams.forEach(
            (team) => {
              const item =
                normalizedData.find(
                  (x) =>
                    String(
                      x.loggedDate,
                    ).slice(
                      0,
                      10,
                    ) === date &&
                    (
                      x.teamLeadName ??
                      'Team'
                    ) === team,
                );

              row[team] =
                Number(
                  item?.idlePercentage ??
                    0,
                );
            },
          );

          return row;
        },
      );
    },
    [
      dates,
      teams,
      normalizedData,
    ],
  );

  /* ==========================================================
   * HORIZONTAL WHEEL SCROLL
   * ========================================================== */

  useEffect(() => {
    const element =
      scrollRef.current;

    if (!element) {
      return;
    }

    const handleWheel = (
      event: WheelEvent,
    ) => {
      const hasOverflow =
        element.scrollWidth >
        element.clientWidth;

      if (!hasOverflow) {
        return;
      }

      const delta =
        Math.abs(
          event.deltaY,
        ) >
        Math.abs(
          event.deltaX,
        )
          ? event.deltaY
          : event.deltaX;

      if (delta === 0) {
        return;
      }

      event.preventDefault();

      element.scrollLeft +=
        delta;
    };

    element.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: false,
      },
    );

    return () => {
      element.removeEventListener(
        'wheel',
        handleWheel,
      );
    };
  }, [chartData.length]);

  /* ==========================================================
   * DRAG START
   * ========================================================== */

  const handleMouseDown =
    useCallback(
      (
        event: React.MouseEvent<HTMLDivElement>,
      ) => {
        const element =
          scrollRef.current;

        if (!element) {
          return;
        }

        if (
          element.scrollWidth <=
          element.clientWidth
        ) {
          return;
        }

        setIsDragging(true);

        dragStartX.current =
          event.clientX;

        dragScrollLeft.current =
          element.scrollLeft;
      },
      [],
    );

  /* ==========================================================
   * DRAG MOVE
   * ========================================================== */

  const handleMouseMove =
    useCallback(
      (
        event: React.MouseEvent<HTMLDivElement>,
      ) => {
        if (!isDragging) {
          return;
        }

        const element =
          scrollRef.current;

        if (!element) {
          return;
        }

        const distance =
          event.clientX -
          dragStartX.current;

        element.scrollLeft =
          dragScrollLeft.current -
          distance;
      },
      [isDragging],
    );

  /* ==========================================================
   * DRAG END
   * ========================================================== */

  const stopDragging =
    useCallback(() => {
      setIsDragging(false);
    }, []);

  /* ==========================================================
   * EMPTY STATE
   * ========================================================== */

  if (
    !normalizedData.length ||
    !chartData.length
  ) {
    return (
      <div
        className="
          flex
          min-h-[360px]
          w-full
          max-w-full
          items-center
          justify-center
          rounded-xl
          border
          border-slate-200
          bg-white
        "
      >
        <div className="text-center">
          <p
            className="
              text-sm
              font-semibold
              text-slate-700
            "
          >
            No Idle Trend Data Available
          </p>

          <p
            className="
              mt-1
              text-xs
              text-slate-400
            "
          >
            No daily idle percentage
            data was returned for
            the selected period.
          </p>
        </div>
      </div>
    );
  }

  /* ==========================================================
   * CHART WIDTH
   * ========================================================== */

  const requiredDateWidth =
    Math.max(
      dates.length *
        DATE_COLUMN_WIDTH,
      0,
    );

  const chartWidth =
    Math.max(
      availableWidth,
      requiredDateWidth,
      300,
    );

  /* ==========================================================
   * DATE FORMAT
   * ========================================================== */

  const formatDate = (
    value: string,
  ) => {
    if (!value) {
      return '';
    }

    const date =
      new Date(
        `${value}T00:00:00`,
      );

    if (
      Number.isNaN(
        date.getTime(),
      )
    ) {
      return value;
    }

    const day =
      String(
        date.getDate(),
      ).padStart(2, '0');

    const month =
      date.toLocaleString(
        'en-US',
        {
          month: 'short',
        },
      );

    return `${day} ${month}`;
  };

  /* ==========================================================
   * Y AXIS
   * ========================================================== */

  const yTicks = [
    0,
    5,
    10,
    15,
    20,
    25,
    30,
  ];

  /* ==========================================================
   * LEGEND
   * ========================================================== */

  const legendPayload: LegendItem[] =
    teams.map(
      (team, index) => ({
        value: team,

        color:
          TEAM_COLORS[
            index %
              TEAM_COLORS.length
          ],
      }),
    );

  /* ==========================================================
   * TARGET POSITION
   *
   * THIS IS THE IMPORTANT FIX.
   *
   * The ReferenceLine and Target badge now use
   * exactly the same plot height.
   *
   * PLOT_HEIGHT = 220px
   *
   * This makes the box sit directly ON the line.
   * ========================================================== */

  const targetTop =
    CHART_MARGIN.top +
    (
      (MAX_Y_VALUE -
        TARGET_PERCENTAGE) /
      MAX_Y_VALUE
    ) *
      PLOT_HEIGHT;

  /* ==========================================================
   * RENDER
   * ========================================================== */

  return (
    <div
      className="
        box-border
        w-full
        max-w-full
        min-w-0
        overflow-hidden
        rounded-xl
        border
        border-slate-200
        bg-white
      "
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        className="
          flex
          w-full
          min-w-0
          flex-wrap
          items-center
          justify-between
          gap-2
          px-4
          pt-3
        "
      >
        <div
          className="
            flex
            min-w-0
            flex-wrap
            items-center
            gap-2
          "
        >
          <h3
            className="
              shrink-0
              text-[14px]
              font-semibold
              text-slate-800
            "
          >
            Daily Idle % Trend
          </h3>

          <span
            className="
              shrink-0
              rounded-full
              bg-blue-50
              px-2.5
              py-1
              text-[9px]
              font-medium
              text-blue-600
            "
          >
            WTD · by team · target
            &lt;{TARGET_PERCENTAGE}%
          </span>
        </div>

        <span
          className="
            shrink-0
            text-[10px]
            font-medium
            text-slate-400
          "
        >
          {dates.length} days
        </span>
      </div>

      {/* ======================================================
          LEGEND
      ====================================================== */}

      <div
        className="
          w-full
          min-w-0
          px-4
          pt-2
        "
      >
        <CustomLegend
          payload={
            legendPayload
          }
          hoveredTeam={
            hoveredTeam
          }
          onHoverTeam={
            setHoveredTeam
          }
        />
      </div>

      {/* ======================================================
          CHART ROW
      ====================================================== */}

      <div
        className="
          box-border
          flex
          w-full
          min-w-0
          max-w-full
          px-3
          pb-3
        "
      >
        {/* ====================================================
            Y AXIS
        ==================================================== */}

        <div
          className="
            relative
            shrink-0
          "
          style={{
            width:
              Y_AXIS_WIDTH,
            height:
              CHART_HEIGHT,
          }}
        >
          {yTicks.map(
            (tick) => {
              const top =
                CHART_MARGIN.top +
                (
                  (MAX_Y_VALUE -
                    tick) /
                  MAX_Y_VALUE
                ) *
                  PLOT_HEIGHT;

              return (
                <span
                  key={tick}
                  className="
                    absolute
                    right-2
                    -translate-y-1/2
                    whitespace-nowrap
                    text-[10px]
                    font-medium
                    text-slate-500
                  "
                  style={{
                    top,
                  }}
                >
                  {tick}%
                </span>
              );
            },
          )}
        </div>

        {/* ====================================================
            VISIBLE CHART VIEWPORT
        ==================================================== */}

        <div
          ref={plotWrapperRef}
          className="
            relative
            min-w-0
            flex-1
            overflow-hidden
          "
          style={{
            height:
              CHART_HEIGHT,
          }}
        >
          {/* ==================================================
              SCROLLABLE CHART
          ================================================== */}

          <div
            ref={scrollRef}
            className={`
              daily-idle-scroll
              absolute
              inset-0
              overflow-x-auto
              overflow-y-hidden
              select-none
              ${
                isDragging
                  ? 'cursor-grabbing'
                  : 'cursor-grab'
              }
            `}
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle:
                'none',
              overscrollBehaviorX:
                'contain',
            }}
            onMouseDown={
              handleMouseDown
            }
            onMouseMove={
              handleMouseMove
            }
            onMouseUp={
              stopDragging
            }
            onMouseLeave={
              stopDragging
            }
          >
            {/* ==================================================
                CHART CONTENT
            ================================================== */}

            <div
              className="
                relative
              "
              style={{
                width:
                  `${chartWidth}px`,
                minWidth:
                  `${chartWidth}px`,
                height:
                  CHART_HEIGHT,
              }}
            >
              <ResponsiveContainer
                width="100%"
                height={
                  CHART_HEIGHT
                }
              >
                <LineChart
                  data={chartData}
                  margin={
                    CHART_MARGIN
                  }
                >
                  {/* =================================================
                      GRID
                  ================================================= */}

                  <CartesianGrid
                    vertical={false}
                    stroke="#E2E8F0"
                    strokeDasharray="3 4"
                  />

                  {/* =================================================
                      X AXIS
                  ================================================= */}

                  <XAxis
                    dataKey="date"
                    axisLine={{
                      stroke:
                        '#CBD5E1',
                    }}
                    tickLine={false}
                    interval={0}
                    tick={{
                      fontSize: 10,
                      fill: '#475569',
                    }}
                    tickFormatter={
                      formatDate
                    }
                    minTickGap={10}
                    height={
                      X_AXIS_HEIGHT
                    }
                  />

                  {/* =================================================
                      Y AXIS
                  ================================================= */}

                  <YAxis
                    hide
                    domain={[
                      0,
                      MAX_Y_VALUE,
                    ]}
                    ticks={yTicks}
                  />

                  {/* =================================================
                      TARGET REFERENCE LINE

                      The badge overlays this line.
                  ================================================= */}

                  <ReferenceLine
                    y={
                      TARGET_PERCENTAGE
                    }
                    stroke="#EF4444"
                    strokeWidth={1.5}
                    strokeDasharray="5 5"
                    ifOverflow="extendDomain"
                  />

                  {/* =================================================
                      TOOLTIP
                  ================================================= */}

                  <Tooltip
                    content={
                      <CustomTooltip />
                    }
                    cursor={{
                      stroke:
                        '#CBD5E1',
                      strokeWidth: 1,
                      strokeDasharray:
                        '4 4',
                    }}
                  />

                  {/* =================================================
                      TEAM LINES
                  ================================================= */}

                  {teams.map(
                    (
                      team,
                      index,
                    ) => {
                      const color =
                        TEAM_COLORS[
                          index %
                            TEAM_COLORS.length
                        ];

                      const isHovered =
                        hoveredTeam ===
                        team;

                      const hasHover =
                        hoveredTeam !==
                        null;

                      const opacity =
                        !hasHover ||
                        isHovered
                          ? 1
                          : 0.15;

                      return (
                        <Line
                          key={team}
                          type="natural"
                          dataKey={team}
                          name={team}
                          stroke={color}
                          strokeWidth={
                            isHovered
                              ? 3
                              : 2
                          }
                          strokeOpacity={
                            opacity
                          }
                          dot={{
                            r: isHovered
                              ? 4
                              : 3,
                            strokeWidth: 1.5,
                            fill: color,
                            opacity,
                          }}
                          activeDot={{
                            r: isHovered
                              ? 6
                              : 5,
                            stroke:
                              '#FFFFFF',
                            strokeWidth: 2,
                            fill: color,
                          }}
                          connectNulls
                          isAnimationActive={
                            false
                          }
                        />
                      );
                    },
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ==================================================
              FIXED TARGET LABEL

              IMPORTANT:

              1. It is OUTSIDE the scrollable chart.
              2. It is positioned relative to the visible chart.
              3. translateY(-50%) centers it vertically.
              4. targetTop uses the SAME plot calculation
                 as the ReferenceLine.
              5. It remains inside the card at all zoom levels.
          ================================================== */}

          <div
            className="
              pointer-events-none
              absolute
              right-[4px]
              z-30
              -translate-y-1/2
            "
            style={{
              top: targetTop,
            }}
          >
            <div
              className="
                flex
                h-[20px]
                min-w-[72px]
                items-center
                justify-center
                rounded-[4px]
                border
                border-red-300
                bg-white
                px-2
                shadow-sm
              "
            >
              <span
                className="
                  whitespace-nowrap
                  text-[10px]
                  font-medium
                  leading-none
                  text-red-500
                "
              >
                Target {TARGET_PERCENTAGE}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================
          HIDE SCROLLBAR
      ======================================================== */}

      <style>
        {`
          .daily-idle-scroll::-webkit-scrollbar {
            width: 0;
            height: 0;
            display: none;
          }

          .daily-idle-scroll {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
        `}
      </style>
    </div>
  );
};

export default DailyIdleTrendChart;